import type { DashboardFilterDataType } from "../../dashboard.types";

export interface FilterWidgetSummary {
  widgetId: string;
  name: string;
  dashboardFilterId?: string;
  dataType?: DashboardFilterDataType;
  parentWidgetId?: string | null;
}

// Only a text filter publishes discrete values a child dimension can be narrowed by.
const canNarrowChildren = (widget: FilterWidgetSummary): boolean => widget.dataType === "text";

const getParentId = (widget: FilterWidgetSummary | undefined): string | undefined =>
  widget?.parentWidgetId ?? undefined;

const reachesWidget = (widgetsById: Map<string, FilterWidgetSummary>, fromId: string, targetId: string): boolean => {
  const visited = new Set<string>();
  let currentId: string | undefined = fromId;
  while (currentId !== undefined && !visited.has(currentId)) {
    if (currentId === targetId) {
      return true;
    }
    visited.add(currentId);
    currentId = getParentId(widgetsById.get(currentId));
  }
  return false;
};

export const getParentFilterOptions = (
  filterWidgets: FilterWidgetSummary[],
  widgetId: string
): FilterWidgetSummary[] => {
  const widgetsById = new Map(filterWidgets.map((widget) => [widget.widgetId, widget]));
  return filterWidgets.filter(
    (widget) =>
      widget.widgetId !== widgetId &&
      widget.dashboardFilterId !== undefined &&
      canNarrowChildren(widget) &&
      !reachesWidget(widgetsById, widget.widgetId, widgetId)
  );
};

export const getFilterWidgetDescendantLevels = (
  filterWidgets: FilterWidgetSummary[],
  widgetId: string
): FilterWidgetSummary[][] => {
  const levels: FilterWidgetSummary[][] = [];
  const visited = new Set([widgetId]);
  let parentIds = new Set([widgetId]);

  while (parentIds.size > 0) {
    const level = filterWidgets.filter(
      (widget) =>
        !visited.has(widget.widgetId) &&
        widget.dashboardFilterId !== undefined &&
        parentIds.has(getParentId(widget) ?? "")
    );
    if (level.length === 0) {
      return levels;
    }
    level.forEach((widget) => visited.add(widget.widgetId));
    levels.push(level);
    parentIds = new Set(level.map((widget) => widget.widgetId));
  }

  return levels;
};
